import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, TrendingUp, TrendingDown } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** Groww-style monthly P&L heatmap.
 *  Props:
 *   - data: [{ date: 'YYYY-MM-DD', net_pnl, gross_profit, loss, entries? }]
 *   - month: Date — any date within the month to show
 *   - onMonthChange(date)
 *   - onDateSelect(date | null)
 *   - selectedDate: Date | null
 */
export const PnlHeatmapCalendar = ({ data = [], month, onMonthChange, onDateSelect, selectedDate }) => {
  const [hovered, setHovered] = useState(null);
  const current = month || new Date();

  const monthKey = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}`;
  const monthLabel = current.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  // Build day → stat map
  const dayMap = useMemo(() => {
    const m = {};
    data.forEach((d) => { if (d?.date) m[d.date] = d; });
    return m;
  }, [data]);

  // Monthly totals
  const monthStats = useMemo(() => {
    let profit = 0, loss = 0, net = 0, winDays = 0, lossDays = 0, flatDays = 0, totalDays = 0;
    Object.entries(dayMap).forEach(([date, d]) => {
      if (!date.startsWith(monthKey)) return;
      profit += Number(d.gross_profit || 0);
      loss += Number(d.loss || 0);
      net += Number(d.net_pnl || 0);
      totalDays += 1;
      if (d.net_pnl > 0) winDays += 1;
      else if (d.net_pnl < 0) lossDays += 1;
      else flatDays += 1;
    });
    return { profit, loss, net, winDays, lossDays, flatDays, totalDays };
  }, [dayMap, monthKey]);

  // Scale for intensity — compute max absolute net in this month
  const scale = useMemo(() => {
    let max = 0;
    Object.entries(dayMap).forEach(([date, d]) => {
      if (!date.startsWith(monthKey)) return;
      const v = Math.abs(Number(d.net_pnl || 0));
      if (v > max) max = v;
    });
    return max || 1;
  }, [dayMap, monthKey]);

  // Build the grid for the month (always 6 rows × 7 cols)
  const grid = useMemo(() => {
    const year = current.getFullYear();
    const mon = current.getMonth();
    const first = new Date(year, mon, 1);
    const daysInMonth = new Date(year, mon + 1, 0).getDate();
    // weekday index (0 = Mon)
    let startIdx = first.getDay() - 1;
    if (startIdx < 0) startIdx = 6;
    const cells = [];
    for (let i = 0; i < startIdx; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${year}-${String(mon + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      cells.push({ day: d, date: dateStr, dayData: dayMap[dateStr] });
    }
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [current, dayMap]);

  const prevMonth = () => onMonthChange?.(new Date(current.getFullYear(), current.getMonth() - 1, 1));
  const nextMonth = () => onMonthChange?.(new Date(current.getFullYear(), current.getMonth() + 1, 1));

  const tileClass = (c) => {
    if (!c) return 'bg-transparent';
    if (!c.dayData) return 'bg-slate-50 text-slate-300';
    const net = Number(c.dayData.net_pnl || 0);
    if (net === 0) return 'bg-slate-100 text-slate-500';
    const intensity = Math.min(1, Math.abs(net) / scale);
    const bucket = Math.min(4, Math.floor(intensity * 5));
    if (net > 0) {
      // Groww green scale
      const greens = ['bg-emerald-50 text-emerald-700', 'bg-emerald-100 text-emerald-800', 'bg-emerald-200 text-emerald-900', 'bg-emerald-400 text-white', 'bg-emerald-600 text-white'];
      return greens[bucket];
    }
    const reds = ['bg-rose-50 text-rose-700', 'bg-rose-100 text-rose-800', 'bg-rose-200 text-rose-900', 'bg-rose-400 text-white', 'bg-rose-600 text-white'];
    return reds[bucket];
  };

  const isSelected = (c) => c && selectedDate && c.date === selectedDate.toISOString().slice(0, 10);
  const todayStr = new Date().toISOString().slice(0, 10);

  const stat = (label, value, tone) => (
    <div className="flex-1 min-w-[80px] rounded-xl bg-white border border-brand-border px-3 py-2">
      <p className="text-[10px] uppercase tracking-widest text-brand-text-secondary font-semibold">{label}</p>
      <p className={`text-sm sm:text-base font-bold ${tone}`}>{value}</p>
    </div>
  );

  return (
    <Card className="border border-brand-border bg-white overflow-hidden" data-testid="pnl-heatmap-calendar">
      <CardContent className="p-4 sm:p-5">
        {/* Month navigator */}
        <div className="flex items-center justify-between mb-4">
          <Button variant="ghost" size="icon" onClick={prevMonth} className="rounded-full h-9 w-9" data-testid="heatmap-prev-month" aria-label="Previous month">
            <ChevronLeft size={18} />
          </Button>
          <div className="text-center">
            <h3 className="font-heading text-base sm:text-lg font-bold text-brand-text-primary">{monthLabel}</h3>
            <p className="text-[11px] text-brand-text-secondary">{monthStats.totalDays} trading day{monthStats.totalDays !== 1 ? 's' : ''}</p>
          </div>
          <Button variant="ghost" size="icon" onClick={nextMonth} className="rounded-full h-9 w-9" data-testid="heatmap-next-month" aria-label="Next month">
            <ChevronRight size={18} />
          </Button>
        </div>

        {/* Monthly summary pills */}
        <div className="flex flex-wrap gap-2 mb-4">
          {stat('Profit', `$${monthStats.profit.toLocaleString(undefined, {maximumFractionDigits: 0})}`, 'text-emerald-600')}
          {stat('Loss', `$${monthStats.loss.toLocaleString(undefined, {maximumFractionDigits: 0})}`, 'text-rose-600')}
          {stat('Net P&L', `${monthStats.net >= 0 ? '+' : ''}$${monthStats.net.toLocaleString(undefined, {maximumFractionDigits: 0})}`, monthStats.net >= 0 ? 'text-emerald-600' : 'text-rose-600')}
          {stat('Win Rate', `${monthStats.totalDays ? Math.round((monthStats.winDays / monthStats.totalDays) * 100) : 0}%`, 'text-brand-primary')}
        </div>

        {/* Day-of-week headers */}
        <div className="grid grid-cols-7 gap-1 sm:gap-1.5 mb-1">
          {DAYS.map((d) => (
            <div key={d} className="text-[10px] uppercase tracking-wider font-semibold text-brand-text-secondary text-center py-1">
              {d}
            </div>
          ))}
        </div>

        {/* Heatmap grid */}
        <div className="grid grid-cols-7 gap-1 sm:gap-1.5">
          {grid.map((c, i) => {
            if (!c) return <div key={`e-${i}`} />;
            const selected = isSelected(c);
            const isToday = c.date === todayStr;
            const title = c.dayData ? `${c.date} · Net ${c.dayData.net_pnl >= 0 ? '+' : ''}$${Number(c.dayData.net_pnl).toFixed(2)}` : `${c.date} · No trades`;
            return (
              <button
                key={c.date}
                title={title}
                onMouseEnter={() => setHovered(c)}
                onMouseLeave={() => setHovered(null)}
                onClick={() => onDateSelect?.(c.dayData ? new Date(c.date + 'T00:00:00') : null)}
                data-testid={`heatmap-day-${c.date}`}
                className={`aspect-square rounded-md flex flex-col items-center justify-center text-[11px] font-semibold transition-all hover:scale-[1.08] hover:shadow-md relative ${tileClass(c)} ${selected ? 'ring-2 ring-brand-primary ring-offset-1' : ''} ${isToday ? 'outline outline-1 outline-dashed outline-brand-primary' : ''}`}
              >
                <span className="leading-none">{c.day}</span>
                {c.dayData && (
                  <span className="text-[8px] font-bold leading-none mt-0.5 opacity-90">
                    {c.dayData.net_pnl >= 0 ? '+' : ''}{Math.round(c.dayData.net_pnl || 0)}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Legend */}
        <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-1.5 text-[10px] text-brand-text-secondary">
            <TrendingDown size={10} className="text-rose-500" />
            <span>Loss</span>
            {['bg-rose-50', 'bg-rose-100', 'bg-rose-200', 'bg-rose-400', 'bg-rose-600'].map((cls) => (
              <div key={cls} className={`w-3 h-3 rounded-sm ${cls}`} />
            ))}
            <div className="w-3 h-3 rounded-sm bg-slate-100 mx-1" />
            {['bg-emerald-50', 'bg-emerald-100', 'bg-emerald-200', 'bg-emerald-400', 'bg-emerald-600'].map((cls) => (
              <div key={cls} className={`w-3 h-3 rounded-sm ${cls}`} />
            ))}
            <TrendingUp size={10} className="text-emerald-600" />
            <span>Profit</span>
          </div>
          <p className="text-[10px] text-brand-text-secondary italic">
            {hovered?.dayData
              ? `${hovered.date} · ${hovered.dayData.net_pnl >= 0 ? '+' : ''}$${Number(hovered.dayData.net_pnl).toFixed(2)}`
              : 'Tap any date for trade details'}
          </p>
        </div>
      </CardContent>
    </Card>
  );
};
